import React, { ReactNode, useContext, useMemo } from "react";
import Select from "react-select";
import countryList from "react-select-country-list";
import { useColorStyle } from "../hooks/useColorStyle";
import { ThemeContext } from "../context/themeContext";

interface CountryOption {
  value: string;
  label: ReactNode;
  code: string;
}

interface ComponentProps {
  onChange: (option: string | null) => void;
  value: string | null;
  name: string;
  placeholder?: string;
  options?: CountryOption[];
  isDisabled?: boolean;
  isLoading?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  menuIsOpen?: boolean;
  selectedValue?: string;
  allowedCountries?: string[]; // New prop to filter countries
}

export const getCountryOptions = (): CountryOption[] =>
  countryList()
    .getData()
    .map((country) => ({
      value: country.label,
      code: country.value,
      label: (
        <div className="flex items-center gap-2" key={country.value}>
          <img
            src={`https://flagcdn.com/w40/${country.value.toLowerCase()}.png`}
            alt={country.label}
            className="w-5 h-3"
          />
          {country.label}
        </div>
      ),
    }));

const CountryDropdown: React.FC<ComponentProps> = ({
  onChange,
  value,
  name,
  allowedCountries = [],
}) => {
  const { theme } = useContext(ThemeContext);
  const { campaignColorStyles } = useColorStyle(theme);
  const options = useMemo(() => getCountryOptions(), []);

  // Map the primitive value (code) back to the full CountryOption object
  const selectedOption = useMemo(
    () => options.find((option) => option.code === value) || null,
    [value, options]
  );

  return (
    <Select
      options={
        allowedCountries.length > 0
          ? options.filter((option) => allowedCountries.includes(option.code))
          : options
      }
      placeholder=""
      value={selectedOption} // Use the mapped option
      onChange={(selectedOption) => {
        onChange(selectedOption?.code || null); // Pass only the code back
      }}
      styles={campaignColorStyles}
      name={name}
    />
  );
};

export default CountryDropdown;
